import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { 
  Building2, Factory, AlertTriangle, FileCheck, FileQuestion, HelpCircle,
  CheckCircle, XCircle, PauseCircle, AlertCircle, ArrowRight, Share2,
  Linkedin, MessageCircle, Clock, DollarSign, Shield, Users, Phone,
  RefreshCcw, Pause, X, Sparkles, TrendingDown, Award, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/22253afe-a0a6-47b8-979b-96783ef6266c/images/bc6f6e828dee1d2885f01672891e1a56a34f3d8557d9baa37faa5a96cee5234f.png";

// Penalty savings data based on years
const PENALTY_DATA = {
  '1-2': { exposure: '₹30,000 – ₹80,000', ccfsCost: '₹15,000 – ₹35,000', savings: '₹15,000 – ₹50,000' },
  '3-5': { exposure: '₹80,000 – ₹2,50,000', ccfsCost: '₹25,000 – ₹80,000', savings: '₹50,000 – ₹1,70,000' },
  '5+': { exposure: '₹1,50,000 – ₹5,00,000+', ccfsCost: '₹40,000 – ₹1,20,000', savings: '₹1,00,000 – ₹4,00,000+' },
};

// Matrix configuration
const BUSINESS_ACTIVITY = [
  { id: 'no_business', label: 'No business ever started' },
  { id: 'business_stopped', label: 'Business stopped earlier' },
  { id: 'loans_investors', label: 'Loans / investors involved' },
];

const COMPLIANCE_STATUS = [
  { id: 'filings_done', label: 'Filings done regularly' },
  { id: 'filings_pending', label: 'Filings pending for some years' },
  { id: 'not_sure', label: 'Not sure / may be inactive' },
];

// Matrix tiles
const MATRIX_TILES = {
  'no_business_filings_done': { 
    title: 'Never operated, compliant',
    desc: 'Company exists but never used',
    recommendation: 'close',
    icon: Building2
  },
  'no_business_filings_pending': { 
    title: 'Never operated, filings due',
    desc: 'Dormant company with pending returns',
    recommendation: 'close',
    icon: FileQuestion
  },
  'no_business_not_sure': { 
    title: 'Never operated, status unclear',
    desc: 'Need to check MCA portal status',
    recommendation: 'review',
    icon: HelpCircle
  },
  'business_stopped_filings_done': { 
    title: 'Operations stopped, compliant',
    desc: 'Business closed but filings current',
    recommendation: 'dormant',
    icon: Pause
  },
  'business_stopped_filings_pending': { 
    title: 'Operations stopped, filings due',
    desc: 'Common situation for inactive companies',
    recommendation: 'close',
    icon: Clock
  },
  'business_stopped_not_sure': { 
    title: 'Operations stopped, status unclear',
    desc: 'Need compliance health check',
    recommendation: 'review',
    icon: AlertCircle
  },
  'loans_investors_filings_done': { 
    title: 'Has stakeholders, compliant',
    desc: 'May have restart potential',
    recommendation: 'restart',
    icon: RefreshCcw
  },
  'loans_investors_filings_pending': { 
    title: 'Has stakeholders, filings due',
    desc: 'Needs careful compliance cleanup',
    recommendation: 'review',
    icon: AlertTriangle
  },
  'loans_investors_not_sure': { 
    title: 'Complex situation',
    desc: 'Multiple factors to evaluate',
    recommendation: 'review',
    icon: AlertTriangle
  },
};

// Risk levels
const RISK_OPTIONS = [
  { id: 'none', label: 'No disputes or liabilities', icon: CheckCircle, color: 'text-green-500' },
  { id: 'some', label: 'Some notices or compliance issues', icon: AlertCircle, color: 'text-amber-500' },
  { id: 'serious', label: 'Bank loans / investors / disputes', icon: XCircle, color: 'text-red-500' },
];

// Results
const RESULTS = {
  close: {
    title: 'Strike-Off / Closure',
    icon: X,
    color: 'bg-red-50 border-red-200',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    description: 'Based on your inputs, your company appears suitable for closure under CCFS-2026.',
    effort: 'Low to Medium',
    costRange: '₹15,000 – ₹50,000',
    costNote: 'depending on pending filings',
    nextSteps: ['Prepare pending filings', 'Clean up compliance issues', 'Apply for strike-off']
  },
  restart: {
    title: 'Restart the Company',
    icon: RefreshCcw,
    color: 'bg-green-50 border-green-200',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    description: 'Your company has potential for revival. Regularize compliance and restart operations.',
    effort: 'Medium',
    costRange: '₹25,000 – ₹75,000',
    costNote: 'depending on compliance gaps',
    nextSteps: ['Clear pending compliances', 'Update registrations', 'Resume business operations']
  },
  dormant: {
    title: 'Convert to Dormant Status',
    icon: Pause,
    color: 'bg-amber-50 border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    description: 'Dormant status allows you to pause operations while keeping the company legally alive.',
    effort: 'Low',
    costRange: '₹10,000 – ₹30,000',
    costNote: 'plus minimal annual filings',
    nextSteps: ['File dormant company application', 'Minimal annual filings', 'Keep company alive for future']
  },
  review: {
    title: 'Needs Expert Review',
    icon: Users,
    color: 'bg-slate-100 border-slate-300',
    iconBg: 'bg-slate-200',
    iconColor: 'text-slate-600',
    description: 'Your situation has factors that require personalized expert guidance.',
    effort: 'Varies',
    costRange: 'To be assessed',
    costNote: 'after detailed review',
    nextSteps: ['Detailed compliance audit', 'Legal review if disputes exist', 'Custom resolution strategy']
  }
};

function getRecommendation(matrixSelection, riskLevel) {
  let baseRec = MATRIX_TILES[matrixSelection]?.recommendation || 'review';
  if (riskLevel === 'serious') return 'review';
  return baseRec;
}

export default function CCFSPage() {
  const [step, setStep] = useState(0);
  const [matrixSelection, setMatrixSelection] = useState(null);
  const [riskLevel, setRiskLevel] = useState(null);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '', company_name: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [penaltyYears, setPenaltyYears] = useState('3-5');

  const handleMatrixSelect = (businessId, complianceId) => {
    const key = `${businessId}_${complianceId}`;
    setMatrixSelection(key);
    setStep(2);
  };

  const handleNotSure = () => {
    setMatrixSelection('not_sure_situation');
    setStep(2);
  };

  const handleRiskSelect = (riskId) => {
    setRiskLevel(riskId);
    setStep(3);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadData.name || !leadData.email || !leadData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const recommendation = getRecommendation(matrixSelection, riskLevel);
      await fetch(`${API_URL}/api/ccfs-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...leadData, quiz_answers: [matrixSelection, riskLevel], recommendation })
      });
      setResult(recommendation);
      setStep(4);
      toast.success('Your company status report is ready!');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetTool = () => {
    setStep(0);
    setMatrixSelection(null);
    setRiskLevel(null);
    setLeadData({ name: '', email: '', phone: '', company_name: '' });
    setResult(null);
  };

  const scrollToTool = () => {
    setStep(1);
    setTimeout(() => document.getElementById('tool')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const shareUrl = encodeURIComponent(window.location.href);
  const shareText = encodeURIComponent("Have an inactive company? Use this 3-minute Company Status Check.");

  return (
    <div className="pt-20" data-testid="ccfs-page">
      {/* 1. HERO SECTION */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInUp">
              <h1 className="font-secondary text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                Sometimes the smartest move in business is to{' '}
                <span className="text-[#E84C3D]">close the chapter.</span>
              </h1>
              <p className="text-xl md:text-2xl text-slate-300 mb-6">
                Not sure whether to <span className="text-red-400">close</span>, <span className="text-green-400">restart</span>, or <span className="text-amber-400">pause</span> your company?
              </p>
              <p className="text-lg text-slate-400 mb-6">
                Use the <strong className="text-white">3-minute Company Status Check.</strong>
              </p>
              
              <div className="bg-slate-800/50 rounded-xl p-5 mb-6">
                <p className="text-slate-300 mb-3">Many founders have companies where:</p>
                <ul className="space-y-2 text-slate-400">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-500 rounded-full"></span>business never started</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-500 rounded-full"></span>operations stopped years ago</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-500 rounded-full"></span>filings are pending</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-500 rounded-full"></span>or MCA status is unclear</li>
                </ul>
                <p className="text-slate-300 mt-3">This tool helps you <strong className="text-white">decide the next step simply.</strong></p>
              </div>
              
              {/* Urgency Banner */}
              <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-300 font-semibold text-sm">CCFS-2026 relief window open until 15 July 2026</p>
                    <p className="text-amber-200/80 text-sm mt-1">Inactive companies can regularize compliance or apply for closure with reduced penalties.</p>
                  </div>
                </div>
              </div>
              
              {/* Savings Hook */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-8">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-green-400" />
                  <p className="text-green-300">Many founders save <strong className="text-green-200">₹50,000 – ₹20,00,000+</strong> in penalties under this scheme.</p>
                </div>
                <p className="text-green-400/70 text-xs mt-2 ml-8">*actual savings depend on years of pending filings</p>
              </div>
              
              <Button onClick={scrollToTool} className="bg-[#E84C3D] hover:bg-[#C0392B] text-white px-8 py-6 text-lg shadow-lg shadow-red-900/30" data-testid="start-check-btn">
                Start Company Check <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            <div className="hidden lg:block">
              <img src={HERO_IMAGE} alt="Founder at crossroads" className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. THREE FOUNDER PATHS */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-4">Three paths forward</h2>
            <p className="text-slate-600">Every inactive company situation leads to one of these outcomes.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Close */}
            <Card className="border-2 border-red-100 hover:border-red-300 hover:shadow-lg transition-all group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                  <X className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Close the Company</h3>
                <p className="text-slate-600 text-sm mb-4">Best when the business is inactive and unlikely to restart.</p>
                <p className="text-red-600 text-sm font-medium">CCFS-2026 allows closure with reduced penalties.</p>
              </CardContent>
            </Card>
            
            {/* Restart */}
            <Card className="border-2 border-green-100 hover:border-green-300 hover:shadow-lg transition-all group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <RefreshCcw className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Restart the Company</h3>
                <p className="text-slate-600 text-sm mb-4">For businesses that stopped operations but may continue again.</p>
                <p className="text-green-600 text-sm font-medium">Compliance cleanup may be required.</p>
              </CardContent>
            </Card>
            
            {/* Dormant */}
            <Card className="border-2 border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                  <Pause className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Make it Dormant</h3>
                <p className="text-slate-600 text-sm mb-4">Best when the business may restart later.</p>
                <p className="text-amber-600 text-sm font-medium">Keeps the company legally alive with minimal compliance.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 3. CCFS PENALTY SAVINGS ESTIMATOR */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-4">CCFS Penalty Savings Estimator</h2>
            <p className="text-slate-600">Companies with pending filings often accumulate large penalties. CCFS-2026 allows you to <strong>regularize compliance or close with reduced penalties</strong>.</p>
          </div>
          
          <Card className="shadow-lg">
            <CardContent className="p-6 md:p-8">
              <div className="mb-8">
                <label className="block text-sm font-medium text-slate-700 mb-4">Years of filings pending</label>
                <div className="flex gap-3">
                  {['1-2', '3-5', '5+'].map((years) => (
                    <button
                      key={years}
                      onClick={() => setPenaltyYears(years)}
                      className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                        penaltyYears === years
                          ? 'bg-[#E84C3D] text-white shadow-md'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {years} years
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-red-50 rounded-xl p-5 text-center">
                  <TrendingDown className="w-6 h-6 text-red-500 mx-auto mb-2" />
                  <p className="text-xs text-red-600 uppercase tracking-wide font-medium mb-1">Penalty exposure without CCFS</p>
                  <p className="text-xl font-bold text-red-700">{PENALTY_DATA[penaltyYears].exposure}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-5 text-center">
                  <FileCheck className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <p className="text-xs text-amber-600 uppercase tracking-wide font-medium mb-1">Estimated cost under CCFS</p>
                  <p className="text-xl font-bold text-amber-700">{PENALTY_DATA[penaltyYears].ccfsCost}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-5 text-center">
                  <Sparkles className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <p className="text-xs text-green-600 uppercase tracking-wide font-medium mb-1">Potential savings</p>
                  <p className="text-xl font-bold text-green-700">{PENALTY_DATA[penaltyYears].savings}</p>
                </div>
              </div>
              
              <p className="text-xs text-slate-500 text-center mt-4">*indicative estimate only — actual amounts depend on specific compliance gaps</p>
              
              <div className="text-center mt-6">
                <Button onClick={scrollToTool} variant="outline" className="border-[#E84C3D] text-[#E84C3D] hover:bg-red-50">
                  Check your company status for a clearer estimate <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 4. TRANSITION TO TOOL */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-4">Not sure which path is right?</h2>
          <p className="text-slate-600 text-lg mb-2">Answer <strong>3 quick questions</strong> and get an instant recommendation.</p>
          <p className="text-slate-500">Most founders complete this check in <strong>under 2 minutes</strong>.</p>
        </div>
      </section>

      {/* 5. COMPANY STATUS CHECK TOOL */}
      <section className="py-16 md:py-20 bg-[#F9F9F7]" id="tool">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-3">3-Minute Company Status Check</h2>
            <p className="text-slate-600">Choose the situation closest to your company.</p>
            <p className="text-slate-500 text-sm mt-1">Don't worry about technical terms.</p>
          </div>

          {/* Matrix Step */}
          {step <= 1 && (
            <Card className="shadow-lg">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-lg font-semibold text-slate-900 mb-6 text-center">Which situation best describes your company?</h3>
                
                <div className="hidden md:grid grid-cols-4 gap-3 mb-3">
                  <div className="text-center"><p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Business Activity ↓</p></div>
                  {COMPLIANCE_STATUS.map((status) => (
                    <div key={status.id} className="text-center"><p className="text-xs font-medium text-slate-600">{status.label}</p></div>
                  ))}
                </div>
                
                <div className="space-y-3">
                  {BUSINESS_ACTIVITY.map((business) => (
                    <div key={business.id} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="flex items-center justify-center md:justify-end md:pr-4">
                        <p className="text-sm font-medium text-slate-700 md:text-right">{business.label}</p>
                      </div>
                      {COMPLIANCE_STATUS.map((compliance) => {
                        const key = `${business.id}_${compliance.id}`;
                        const tile = MATRIX_TILES[key];
                        const Icon = tile?.icon || HelpCircle;
                        const isSelected = matrixSelection === key;
                        return (
                          <button
                            key={key}
                            onClick={() => handleMatrixSelect(business.id, compliance.id)}
                            className={`p-4 border-2 rounded-xl transition-all text-left group hover:border-[#E84C3D] hover:shadow-md ${
                              isSelected ? 'border-[#E84C3D] bg-red-50 shadow-md' : 'border-slate-200 bg-white'
                            }`}
                            data-testid={`matrix-${key}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                isSelected ? 'bg-[#E84C3D] text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-red-100 group-hover:text-[#E84C3D]'
                              }`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-800 leading-tight">{tile?.title}</p>
                                <p className="text-xs text-slate-500 mt-1">{tile?.desc}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-6 border-t">
                  <button onClick={handleNotSure} className="w-full p-4 border-2 border-dashed border-slate-300 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition-all text-center" data-testid="not-sure-btn">
                    <p className="text-slate-600 font-medium">I'm not sure about my situation</p>
                    <p className="text-slate-500 text-sm mt-1">Skip to speak with an advisor</p>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risk Step */}
          {step === 2 && (
            <Card className="shadow-lg">
              <CardContent className="p-6 md:p-10">
                <p className="text-sm text-slate-500 mb-2">Step 2 of 3</p>
                <h3 className="text-xl font-semibold text-slate-900 mb-8">Any financial or regulatory complications?</h3>
                <div className="space-y-4">
                  {RISK_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button key={option.id} onClick={() => handleRiskSelect(option.id)} className="w-full p-5 border-2 border-slate-200 rounded-xl hover:border-[#E84C3D] hover:bg-red-50/50 transition-all text-left flex items-center gap-4 group" data-testid={`risk-${option.id}`}>
                        <div className={`w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-white ${option.color}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className="text-slate-700 font-medium group-hover:text-slate-900">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => setStep(1)} className="mt-6 text-slate-500 hover:text-slate-700 text-sm">← Go back</button>
              </CardContent>
            </Card>
          )}

          {/* Lead Capture */}
          {step === 3 && (
            <Card className="shadow-lg">
              <CardContent className="p-6 md:p-10">
                <p className="text-sm text-slate-500 mb-2">Step 3 of 3</p>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Almost done!</h3>
                <p className="text-slate-600 mb-8">Where should we send your company status report?</p>
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <Input value={leadData.name} onChange={(e) => setLeadData({ ...leadData, name: e.target.value })} placeholder="Your name" className="h-12" required data-testid="lead-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <Input type="email" value={leadData.email} onChange={(e) => setLeadData({ ...leadData, email: e.target.value })} placeholder="your@email.com" className="h-12" required data-testid="lead-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                    <Input value={leadData.phone} onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })} placeholder="+91 98765 43210" className="h-12" required data-testid="lead-phone" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Company Name (optional)</label>
                    <Input value={leadData.company_name} onChange={(e) => setLeadData({ ...leadData, company_name: e.target.value })} placeholder="Your company name" className="h-12" data-testid="lead-company" />
                  </div>
                  <Button type="submit" className="w-full bg-[#E84C3D] hover:bg-[#C0392B] py-6 text-lg mt-6" disabled={loading} data-testid="show-result-btn">
                    {loading ? 'Processing...' : 'Show My Result'} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
                <button onClick={() => setStep(2)} className="mt-4 text-slate-500 hover:text-slate-700 text-sm block mx-auto">← Go back</button>
              </CardContent>
            </Card>
          )}

          {/* Result */}
          {step === 4 && result && (
            <div className="space-y-6">
              <Card className={`shadow-lg border-2 ${RESULTS[result].color}`}>
                <CardContent className="p-6 md:p-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl ${RESULTS[result].iconBg} flex items-center justify-center`}>
                      {(() => { const Icon = RESULTS[result].icon; return <Icon className={`w-8 h-8 ${RESULTS[result].iconColor}`} />; })()}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Recommended Path</p>
                      <h3 className="text-2xl font-bold text-slate-900">{RESULTS[result].title}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">{RESULTS[result].description}</p>
                  <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b">
                    <div className="bg-white/50 rounded-lg p-4">
                      <p className="text-sm text-slate-500">Estimated Effort</p>
                      <p className="font-semibold text-slate-900">{RESULTS[result].effort}</p>
                    </div>
                    <div className="bg-white/50 rounded-lg p-4">
                      <p className="text-sm text-slate-500">Estimated Cost Range</p>
                      <p className="font-semibold text-slate-900">{RESULTS[result].costRange}</p>
                      <p className="text-xs text-slate-500">{RESULTS[result].costNote}</p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <p className="font-medium text-slate-900 mb-3">Ratnatrraya can help you:</p>
                    <ul className="space-y-2">
                      {RESULTS[result].nextSteps.map((s, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-600"><CheckCircle className="w-5 h-5 text-green-500 shrink-0" />{s}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 mb-6">
                    <p className="text-slate-700 text-sm"><strong>Most founders prefer a quick 15-minute call</strong> to confirm the exact steps before proceeding.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to="/contact" className="flex-1"><Button className="w-full bg-[#E84C3D] hover:bg-[#C0392B] py-6"><Phone className="w-4 h-4 mr-2" />Speak to a Ratnatrraya Advisor</Button></Link>
                    <Button variant="outline" className="flex-1 py-6" onClick={() => toast.info('Report download coming soon!')}>Download My Report</Button>
                  </div>
                  <p className="text-center text-slate-500 text-sm mt-4"><Shield className="w-4 h-4 inline mr-1" />Your situation will remain confidential.</p>
                </CardContent>
              </Card>
              <Card className="shadow-md">
                <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3"><Share2 className="w-5 h-5 text-slate-500" /><span className="text-slate-600">Know someone with an inactive company?</span></div>
                  <div className="flex gap-3">
                    <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#0077B5] text-white rounded-lg hover:bg-[#006699] transition-colors"><Linkedin className="w-5 h-5" /></a>
                    <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#25D366] text-white rounded-lg hover:bg-[#20BD5A] transition-colors"><MessageCircle className="w-5 h-5" /></a>
                  </div>
                </CardContent>
              </Card>
              <div className="text-center"><button onClick={resetTool} className="text-slate-500 hover:text-slate-700 text-sm underline">Take the assessment again</button></div>
            </div>
          )}
        </div>
      </section>

      {/* 7. TRUST & EXPERIENCE */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-4">Experience founders can rely on</h2>
            <p className="text-slate-600">Ratnatrraya together with <a href="https://samistilegal.in/" target="_blank" rel="noopener noreferrer" className="text-[#E84C3D] hover:underline">Samisti Legal</a> has handled <strong>30+ company closure and restructuring cases</strong>, including:</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-xl p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center"><X className="w-6 h-6 text-red-600" /></div>
              <p className="text-sm font-medium text-slate-700">Strike-off applications</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center"><Briefcase className="w-6 h-6 text-amber-600" /></div>
              <p className="text-sm font-medium text-slate-700">Voluntary winding-up</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center"><Pause className="w-6 h-6 text-blue-600" /></div>
              <p className="text-sm font-medium text-slate-700">Dormant conversions</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center"><FileCheck className="w-6 h-6 text-green-600" /></div>
              <p className="text-sm font-medium text-slate-700">Compliance cleanup</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. CCFS EXPLANATION */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-6">What is CCFS-2026?</h2>
              <p className="text-slate-600 mb-6">The <strong>Companies Compliance Facilitation Scheme</strong> allows companies with pending filings or inactive status to regularize compliance with reduced penalties.</p>
              <p className="text-slate-600 mb-6">Founders can use this window to:</p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700"><div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center"><X className="w-4 h-4 text-red-600" /></div>Close inactive companies</li>
                <li className="flex items-center gap-3 text-slate-700"><div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center"><Pause className="w-4 h-4 text-amber-600" /></div>Convert companies to dormant status</li>
                <li className="flex items-center gap-3 text-slate-700"><div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center"><RefreshCcw className="w-4 h-4 text-green-600" /></div>Complete pending compliance and restart</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="font-semibold text-lg text-slate-900 mb-6">Why founders choose Ratnatrraya</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4"><div className="w-10 h-10 rounded-lg bg-[#E84C3D]/10 flex items-center justify-center shrink-0"><CheckCircle className="w-5 h-5 text-[#E84C3D]" /></div><div><p className="font-medium text-slate-900">Clear step-by-step process</p><p className="text-sm text-slate-500">Know exactly what happens next</p></div></div>
                <div className="flex items-start gap-4"><div className="w-10 h-10 rounded-lg bg-[#E84C3D]/10 flex items-center justify-center shrink-0"><DollarSign className="w-5 h-5 text-[#E84C3D]" /></div><div><p className="font-medium text-slate-900">Transparent cost estimates</p><p className="text-sm text-slate-500">No hidden fees or surprises</p></div></div>
                <div className="flex items-start gap-4"><div className="w-10 h-10 rounded-lg bg-[#E84C3D]/10 flex items-center justify-center shrink-0"><FileCheck className="w-5 h-5 text-[#E84C3D]" /></div><div><p className="font-medium text-slate-900">Compliance cleanup support</p><p className="text-sm text-slate-500">We handle the paperwork</p></div></div>
                <div className="flex items-start gap-4"><div className="w-10 h-10 rounded-lg bg-[#E84C3D]/10 flex items-center justify-center shrink-0"><Shield className="w-5 h-5 text-[#E84C3D]" /></div><div><p className="font-medium text-slate-900">Confidential advisory</p><p className="text-sm text-slate-500">Your privacy is protected</p></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FINAL CTA */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-secondary text-2xl md:text-3xl font-semibold mb-4">Clean up the past. Move forward with clarity.</h2>
          <p className="text-slate-400 mb-8 text-lg">Use the Company Status Check or speak with a Ratnatrraya advisor.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => { resetTool(); setTimeout(() => document.getElementById('tool')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="bg-[#E84C3D] hover:bg-[#C0392B] px-8 py-6 text-lg">Start Company Check <ArrowRight className="ml-2 h-5 w-5" /></Button>
            <Link to="/contact"><Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg w-full sm:w-auto">Book Consultation</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
